import { FC, useCallback, useRef, useEffect } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import {
  FormControl,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  WbDatePicker,
  WbButton,
  WbAlert,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@mainstage/core-plugin-api';
import { useCreateAccessToken, CreateAccessTokenRequest } from '../../hooks';
import { AccessToken } from '@agilelab/plugin-wb-auth-backend';

const CreateAccessTokenSchema = z
  .object({
    expirationDate: z.string().optional(),
    noExpiration: z.boolean().optional(),
  })
  .refine(
    data => {
      return (
        data.noExpiration ||
        (data.expirationDate && data.expirationDate.trim() !== '')
      );
    },
    {
      message: 'Please select an expiration date or check "No Expiration date"',
      path: ['expirationDate'],
    },
  );

type CreateAccessTokenFormValues = z.infer<typeof CreateAccessTokenSchema>;

const useStyles = makeStyles(theme => ({
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    alignItems: 'center',
    gap: 16,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '3fr 1fr',
    },
  },
  formInputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: 4,
    },
  },
  formControl: {
    flex: '1 1 auto',
  },
  datePickerInput: {
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'center',
    justifySelf: 'flex-end',
  },
}));

interface CreateAccessTokenFormProps {
  onTokenCreated?: (token: AccessToken) => void;
  /**
   * Entity reference for which to create the access token.
   */
  entityRef: string;
}

export const CreateAccessTokenForm: FC<CreateAccessTokenFormProps> = ({
  onTokenCreated,
  entityRef,
}) => {
  const classes = useStyles();
  const alertApi = useApi(customAlertApiRef);

  const form = useForm<CreateAccessTokenFormValues>({
    defaultValues: {
      expirationDate: '',
      noExpiration: false,
    },
    mode: 'onChange',
    resolver: zodResolver(CreateAccessTokenSchema),
  });

  const {
    isLoading: creating,
    error: createError,
    mutate: createFn,
  } = useCreateAccessToken({ alertOnError: false });

  const formRef = useRef<HTMLFormElement>(null);

  const handleProgrammaticSubmit = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const onSubmit: SubmitHandler<CreateAccessTokenFormValues> = useCallback(
    data => {
      const expirationIso =
        !data.noExpiration && data.expirationDate
          ? new Date(data.expirationDate).toISOString()
          : undefined;

      const request: CreateAccessTokenRequest = {
        expirationDate: expirationIso,
        accountRef: entityRef,
      };

      createFn(request, {
        onSuccess: token => {
          form.reset();
          onTokenCreated?.(token);
          alertApi.post({
            severity: 'success',
            message: 'Access token created successfully',
          });
        },
      });
    },
    [alertApi, createFn, entityRef, form, onTokenCreated],
  );

  const isValid = form.formState.isValid;
  const noExpiration = form.watch('noExpiration');

  useEffect(() => {
    if (noExpiration) {
      form.resetField('expirationDate');
    }
  }, [noExpiration, form]);

  return (
    <Box>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        {createError instanceof Error && (
          <WbAlert
            variant="collapsible"
            message={{
              title: 'Error',
              type: 'error',
              content: <Typography>{createError.message}</Typography>,
            }}
          />
        )}
        <div className={classes.formGrid}>
          <Box className={classes.formInputsContainer}>
            <Controller
              name="expirationDate"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <FormControl className={classes.formControl}>
                  <WbDatePicker
                    label="Expiration date"
                    placeholder="Select an expiration date (exclusive)"
                    id="expirationDate"
                    value={field.value}
                    error={!!error}
                    onChange={val => field.onChange(val ?? '')}
                    helperText={error?.message}
                    disabled={noExpiration}
                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                    className={classes.datePickerInput}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </FormControl>
              )}
            />
            <Controller
              name="noExpiration"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label="No Expiration date"
                />
              )}
            />
          </Box>
          <Box className={classes.buttonContainer}>
            <WbButton
              label="Generate token"
              onClick={handleProgrammaticSubmit}
              disabled={!isValid || creating}
              loading={creating}
              variant="contained"
              color="primary"
            />
          </Box>
        </div>
      </form>
    </Box>
  );
};

CreateAccessTokenForm.displayName = 'CreateAccessTokenForm';
